import { Injectable } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { TranslationsLibService } from '@nutricontrol/app360-shared';
import { RemoteControlState } from '../remote-control.model';
import { Subscription, timer } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class MarqueeService {
  private marqueeWidth = 40;
  private stableMessage = '';
  private marqueeOld = '';
  private marqueeNew = '';
  private isTransitioning = false;
  private pendingMessages: { message: string; css: string }[] = [];
  private currentSpeed = 50;

  private readonly marqueeRegularTimer = 50;
  private readonly marqueeHurryTimer = 20;

  private marqueeTimer: Subscription = new Subscription();

  private stableMessageClass = 'state-default';
  private newMessageClass = 'state-default';
  private lastStatusKey = '';

  constructor(
    private sanitizer: DomSanitizer,
    private translationsLibService: TranslationsLibService
  ) {}

  public get computedMarqueeHTML(): SafeHtml {
    let html: string;
    if (!this.isTransitioning) {
      html = `<span class="${this.stableMessageClass}">${this.marqueeOld}</span>`;
    } else {
      html =
        `<span class="${this.stableMessageClass}">${this.marqueeOld}</span>` +
        `<span class="${this.newMessageClass}">${this.marqueeNew}</span>`;
    }
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  public init(): void {
    this.stableMessage = this.centerText('');
    this.marqueeOld = this.stableMessage;
    this.marqueeNew = '';
    this.isTransitioning = false;
    this.stableMessageClass = 'state-default';
    this.newMessageClass = 'state-default';
    this.lastStatusKey = '';
  }

  public updateStatusState(state: RemoteControlState): void {
    const key = this.computeStatusKey(state);
    const msg = key ? this.translationsLibService.get(key) : '';
    const centered = this.centerText(msg);
    const css = this.getCssClassFromStatus(key);

    if (key !== this.lastStatusKey && key !== '') {
      this.limitPendingMessages();
      this.pendingMessages.push({ message: centered, css });
      this.lastStatusKey = key;
      if (!this.isTransitioning) {
        this.startNextTransition();
      }
    }
  }

  public cleanup(): void {
    if (this.marqueeTimer && !this.marqueeTimer.closed) {
      this.marqueeTimer.unsubscribe();
    }
  }

  private computeStatusKey(state: RemoteControlState): string {
    if (!state.terminalConnected) return 'remote_control_is_disconnected';
    if (state.connectionErrors) return 'remote_control_has_connection_errors';
    if (state.sendingButton) return 'remote_control_is_sending_button';
    if (state.readingScreen) return 'remote_control_is_reading_screen';
    if (state.queueButtons > 0) return 'remote_control_is_pending';
    if (state.ready) return 'remote_control_is_ready';
    return '';
  }

  private getCssClassFromStatus(key: string): string {
    switch (key) {
      case 'remote_control_is_disconnected':
      case 'remote_control_has_connection_errors':
        return 'state-error';
      case 'remote_control_is_sending_button':
        return 'state-sending';
      case 'remote_control_is_reading_screen':
        return 'state-reading';
      case 'remote_control_is_pending':
        return 'state-pending';
      case 'remote_control_is_ready':
        return 'state-ready';
      default:
        return 'state-default';
    }
  }

  private centerText(text: string): string {
    const t = text.replace(/\u00A0/g, ' ').trim();
    if (t.length >= this.marqueeWidth) return t.slice(0, this.marqueeWidth);
    const spaces = this.marqueeWidth - t.length;
    const left = Math.floor(spaces / 2);
    return t.padStart(t.length + left, ' ').padEnd(this.marqueeWidth, ' ');
  }

  private startNextTransition(): void {
    const next = this.pendingMessages.shift();
    if (!next) return;
    const oldCss = this.stableMessageClass;
    const newCss = next.css;
    this.newMessageClass = newCss;

    this.startMarqueeTransition(next.message, oldCss, newCss);
  }

  private startMarqueeTransition(
    newMessage: string,
    oldCss: string,
    newCss: string
  ): void {
    const oldMessage = this.stableMessage;
    this.isTransitioning = true;
    const duration = this.marqueeWidth * this.currentSpeed;
    const start = Date.now();

    const scheduleTick = () => {
      // Cancelar la suscripción anterior antes de crear una nueva
      if (this.marqueeTimer && !this.marqueeTimer.closed) {
        this.marqueeTimer.unsubscribe();
      }
      // Programar el siguiente tick con RxJS timer()
      this.marqueeTimer = timer(this.currentSpeed).subscribe(() => tick());
    };

    const tick = () => {
      const elapsed = Date.now() - start;
      const progress = Math.min(elapsed / duration, 1);
      const i = Math.floor(progress * this.marqueeWidth);

      if (i >= this.marqueeWidth) {
        // Fin de la transición
        this.stableMessage = newMessage;
        this.marqueeOld = newMessage;
        this.marqueeNew = '';
        this.isTransitioning = false;
        this.stableMessageClass = newCss;
        this.currentSpeed = this.marqueeRegularTimer;
        if (this.pendingMessages.length > 0) {
          this.currentSpeed =
            this.pendingMessages.length > 1
              ? this.marqueeHurryTimer
              : this.marqueeRegularTimer;
          this.startNextTransition();
        }
      } else {
        // Intermedio: actualizar cadenas y reprogramar
        this.marqueeOld = oldMessage.substring(i);
        this.marqueeNew = newMessage.substring(0, i);
        this.stableMessageClass = oldCss;
        this.newMessageClass = newCss;
        scheduleTick();
      }
    };

    // Inicia el primer tick
    scheduleTick();
  }

  private limitPendingMessages(): void {
    // Limitar el número de mensajes pendientes a 3
    if (this.pendingMessages.length > 3) {
      this.pendingMessages.splice(0, this.pendingMessages.length - 3);
    }
  }
}
